import { useAuth } from "../../hooks/useAuth";
import { useCrypto } from "../../hooks/useCrypto";
import Header from "../../components/feature/Header";
import Button from "../../components/base/Button";
import Card from "../../components/base/Card";
import { useNavigate, Link } from "react-router-dom";

export default function Home() {
  const { isAuthenticated, login, logout, user } = useAuth();
  const { cryptoData, isLoading } = useCrypto();
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate("/register");
  };

  const handleLogin = () => {
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section
        className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 text-white py-20"
        style={{
          backgroundImage:
            "url(https://readdy.ai/api/search-image?query=Modern%20cryptocurrency%20trading%20platform%20interface%20with%20digital%20charts%2C%20blockchain%20network%20visualization%2C%20futuristic%20financial%20technology%20background%2C%20blue%20and%20purple%20gradient%20colors%2C%20professional%20trading%20environment%2C%20high-tech%20atmosphere%2C%20clean%20minimalist%20design&width=1920&height=800&seq=hero001&orientation=landscape)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-blue-900/70"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Trade Crypto with
              <span className="text-blue-300"> Confidence</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100 max-w-3xl mx-auto">
              Professional cryptocurrency trading platform with real-time data,
              advanced analytics, and secure wallet management
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={handleGetStarted}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Start Trading Now
              </Button>
              <Button
                size="lg"
                variant="secondary"
                className="bg-white/10 hover:bg-white/20 text-white border border-white/30"
              >
                View Live Prices
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Live Crypto Prices */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Live Cryptocurrency Prices
            </h2>
            <p className="text-gray-600 text-lg">
              Real-time market data for top cryptocurrencies
            </p>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <div className="h-20 bg-gray-200 rounded"></div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cryptoData.slice(0, 6).map((crypto) => (
                <Card
                  key={crypto.id}
                  className="hover:shadow-lg transition-shadow cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <img
                        src={crypto.image}
                        alt={crypto.name}
                        className="w-12 h-12 rounded-full"
                      />
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {crypto.name}
                        </h3>
                        <p className="text-gray-500 text-sm">{crypto.symbol}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">
                        ${crypto.price.toLocaleString()}
                      </p>
                      <p
                        className={`text-sm ${
                          crypto.change24h >= 0
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {crypto.change24h >= 0 ? "+" : ""}
                        {crypto.change24h.toFixed(2)}%
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Choose CryptoTrade Pro?
            </h2>
            <p className="text-gray-600 text-lg">
              Advanced features for professional crypto trading
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-line-chart-line text-blue-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold mb-3">Real-Time Trading</h3>
              <p className="text-gray-600">
                Execute trades instantly with live market data and advanced
                charting tools
              </p>
            </Card>

            <Card className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-green-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold mb-3">Secure Wallet</h3>
              <p className="text-gray-600">
                Multi-layer security with cold storage and 2FA protection for
                your assets
              </p>
            </Card>

            <Card className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-exchange-line text-purple-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold mb-3">Instant Conversion</h3>
              <p className="text-gray-600">
                Convert between cryptocurrencies instantly with competitive
                rates
              </p>
            </Card>

            <Card className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-pie-chart-line text-orange-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold mb-3">
                Portfolio Analytics
              </h3>
              <p className="text-gray-600">
                Track performance with detailed analytics and profit/loss
                reports
              </p>
            </Card>

            <Card className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-notification-line text-red-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold mb-3">Price Alerts</h3>
              <p className="text-gray-600">
                Set custom alerts for price movements and never miss trading
                opportunities
              </p>
            </Card>

            <Card className="text-center">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-line text-indigo-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold mb-3">24/7 Support</h3>
              <p className="text-gray-600">
                Round-the-clock customer support to help you with any trading
                questions
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Start Trading?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of traders who trust CryptoTrade Pro for their
            cryptocurrency investments
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button
                  size="lg"
                  className="bg-white text-blue-600 hover:bg-gray-100"
                >
                  Go to Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button
                  size="lg"
                  className="bg-white text-[blue] hover:bg-gray-100"
                >
                  Sign In to Trade
                </Button>
              </Link>
            )}
            <Button
              size="lg"
              className="bg-blue-700 hover:bg-blue-800 text-white border border-blue-500"
            >
              Learn More
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <i className="ri-currency-line text-white"></i>
                </div>
                <span
                  className="text-xl font-bold"
                  style={{ fontFamily: '"Pacifico", serif' }}
                >
                  CryptoTrade Pro
                </span>
              </div>
              <p className="text-gray-400">
                Professional cryptocurrency trading platform for modern
                investors.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Platform</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <span className="cursor-not-allowed opacity-50">
                    Spot Trading
                  </span>
                </li>
                <li>
                  <span className="cursor-not-allowed opacity-50">Convert</span>
                </li>
                <li>
                  <span className="cursor-not-allowed opacity-50">Markets</span>
                </li>
                <li>
                  <span className="cursor-not-allowed opacity-50">Charts</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Account</h3>
              <ul className="space-y-2 text-gray-400">
                {isAuthenticated ? (
                  <>
                    <li>
                      <Link to="/wallet" className="hover:text-white">
                        Wallet
                      </Link>
                    </li>
                    <li>
                      <Link to="/dashboard" className="hover:text-white">
                        Portfolio
                      </Link>
                    </li>
                    <li>
                      <span className="cursor-not-allowed opacity-50">
                        Security
                      </span>
                    </li>
                    <li>
                      <span className="cursor-not-allowed opacity-50">
                        Settings
                      </span>
                    </li>
                  </>
                ) : (
                  <>
                    <li>
                      <span className="cursor-not-allowed opacity-50">
                        Wallet
                      </span>
                    </li>
                    <li>
                      <span className="cursor-not-allowed opacity-50">
                        Portfolio
                      </span>
                    </li>
                    <li>
                      <span className="cursor-not-allowed opacity-50">
                        Security
                      </span>
                    </li>
                    <li>
                      <span className="cursor-not-allowed opacity-50">
                        Settings
                      </span>
                    </li>
                  </>
                )}
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <span className="cursor-not-allowed opacity-50">
                    Help Center
                  </span>
                </li>
                <li>
                  <span className="cursor-not-allowed opacity-50">
                    Contact Us
                  </span>
                </li>
                <li>
                  <span className="cursor-not-allowed opacity-50">
                    API Docs
                  </span>
                </li>
                <li>
                  <span className="cursor-not-allowed opacity-50">
                    System Status
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400">
              © 2025 CryptoTrade Pro. All rights reserved.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <span className="text-gray-400 cursor-not-allowed opacity-50">
                Terms
              </span>
              <span className="text-gray-400 cursor-not-allowed opacity-50">
                Privacy
              </span>
              <a
                href="https://readdy.ai/?origin=logo"
                className="text-gray-400 hover:text-white"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
